/**
 * BeginbalansPage - Beginbalans beheer
 * EXACT kopie van Morris templates/beginbalans.html
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import {
  boekhoudingService,
  BeginbalansData,
  BeginbalansItem,
  BOEKJAAR_OPTIES,
} from '../../services/boekhouding.service';

const BeginbalansPage = () => {
  const [loading, setLoading] = useState(true);
  const [jaar, setJaar] = useState(() => {
    return parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());
  });
  const [data, setData] = useState<BeginbalansData | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBedrag, setEditBedrag] = useState('');

  // Nieuw item formulier
  const [nieuwType, setNieuwType] = useState<'activa' | 'passiva'>('activa');
  const [nieuwCategorie, setNieuwCategorie] = useState('liquide_middelen');
  const [nieuwOmschrijving, setNieuwOmschrijving] = useState('');
  const [nieuwBedrag, setNieuwBedrag] = useState('');

  // Categorie opties per type - EXACT Morris
  const activaCategorieen = [
    { value: 'liquide_middelen', label: 'Liquide middelen' },
    { value: 'vaste_activa', label: 'Vaste activa' },
    { value: 'vorderingen', label: 'Vorderingen' },
    { value: 'btw', label: 'Omzetbelasting' },
  ];

  const passivaCategorieen = [
    { value: 'eigen_vermogen', label: 'Eigen vermogen' },
    { value: 'reserves', label: 'Reserves (Oudedagsreserve)' },
    { value: 'langlopende_schulden', label: 'Langlopende schulden' },
    { value: 'kortlopende_schulden', label: 'Kortlopende schulden' },
    { value: 'schulden', label: 'Crediteuren' },
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await boekhoudingService.getBeginbalans(jaar);
      setData(response);
    } catch (err) {
      console.error('Error loading beginbalans:', err);
    } finally {
      setLoading(false);
    }
  }, [jaar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update categorie bij type wijziging
  useEffect(() => {
    if (nieuwType === 'activa') {
      setNieuwCategorie('liquide_middelen');
    } else {
      setNieuwCategorie('eigen_vermogen');
    }
  }, [nieuwType]);

  const formatBedrag = (bedrag: number): string => {
    const prefix = bedrag < 0 ? '-' : '';
    const abs = Math.abs(bedrag);
    return `${prefix}€ ${abs.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleBewerk = (item: BeginbalansItem) => {
    setEditingId(item.id);
    setEditBedrag(item.bedrag.toString());
  };

  const handleAnnuleerBewerk = () => {
    setEditingId(null);
    setEditBedrag('');
  };

  const handleOpslaanBewerk = async (id: number) => {
    try {
      await boekhoudingService.updateBeginbalansItem(id, parseFloat(editBedrag));
      setMsg('Item gewijzigd');
      setEditingId(null);
      loadData();
    } catch (err) {
      console.error('Error updating item:', err);
    }
  };

  const handleVerwijder = async (id: number) => {
    if (!window.confirm('Item verwijderen?')) return;
    try {
      await boekhoudingService.deleteBeginbalansItem(id);
      setMsg('Item verwijderd');
      loadData();
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleNieuwItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await boekhoudingService.createBeginbalansItem({
        jaar,
        type: nieuwType,
        categorie: nieuwCategorie,
        omschrijving: nieuwOmschrijving,
        bedrag: parseFloat(nieuwBedrag),
      });
      setMsg('Item toegevoegd');
      setNieuwOmschrijving('');
      setNieuwBedrag('');
      loadData();
    } catch (err) {
      console.error('Error creating item:', err);
    }
  };

  const handleOphalenVorigJaar = async () => {
    if (!window.confirm(`Beginbalans ophalen uit eindbalans ${jaar - 1}? Dit vervangt de huidige beginbalans.`)) return;
    try {
      const result = await boekhoudingService.ophalenVorigJaar(jaar);
      setMsg(result.message);
      loadData();
    } catch (err) {
      console.error('Error ophalen vorig jaar:', err);
    }
  };

  // Render items voor een categorie - EXACT Morris render_items macro
  const renderItems = (items: BeginbalansItem[]) => {
    return items.map(item => (
      editingId === item.id ? (
        // Edit row
        <tr key={`edit-${item.id}`}>
          <td colSpan={3}>
            <form
              onSubmit={(e) => { e.preventDefault(); handleOpslaanBewerk(item.id); }}
              style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}
            >
              <span style={{ flex: 1 }}>{item.omschrijving}</span>
              <input
                type="number"
                step="0.01"
                value={editBedrag}
                onChange={(e) => setEditBedrag(e.target.value)}
                style={{ width: '120px', padding: '0.25rem' }}
              />
              <button type="submit" className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}>OK</button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
                onClick={handleAnnuleerBewerk}
              >
                X
              </button>
            </form>
          </td>
        </tr>
      ) : (
        // View row
        <tr key={item.id}>
          <td>{item.omschrijving}</td>
          <td style={{ textAlign: 'right' }} className={item.bedrag < 0 ? styles.negative : ''}>
            {formatBedrag(item.bedrag)}
          </td>
          <td style={{ width: '140px' }}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
              onClick={() => handleBewerk(item)}
            >
              Bewerk
            </button>
            <form
              style={{ display: 'inline', marginLeft: '0.25rem' }}
              onSubmit={(e) => { e.preventDefault(); handleVerwijder(item.id); }}
            >
              <button type="submit" className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}>X</button>
            </form>
          </td>
        </tr>
      )
    ));
  };

  // Categorie header component
  const renderCategorieSection = (
    _categorie: string,
    items: BeginbalansItem[] | undefined,
    label: string
  ) => {
    if (!items || items.length === 0) return null;
    return (
      <>
        <h4 className={styles.sectionHeader}>{label}</h4>
        <table className={`${styles.dataTable} ${styles.compact}`}>
          <tbody>
            {renderItems(items)}
          </tbody>
        </table>
      </>
    );
  };

  if (loading) {
    return (
      <BoekhoudingLayout
        title={`Beginbalans per 1-1-${jaar}`}
        headerActions={
          <Link to="/boekhouding/instellingen" className={`${styles.btn} ${styles.btnSecondary}`}>
            Terug naar Instellingen
          </Link>
        }
      >
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout
      title={`Beginbalans per 1-1-${jaar}`}
      headerActions={
        <>
          <select
            value={jaar}
            onChange={(e) => {
              const newJaar = parseInt(e.target.value);
              setJaar(newJaar);
              localStorage.setItem('boekhouding_jaar', newJaar.toString());
            }}
            style={{ marginRight: '0.5rem', padding: '0.4rem' }}
          >
            {BOEKJAAR_OPTIES.map(j => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleOphalenVorigJaar}
            style={{ marginRight: '0.5rem' }}
          >
            Ophalen uit {jaar - 1}
          </button>
          <Link to="/boekhouding/instellingen" className={`${styles.btn} ${styles.btnSecondary}`}>
            Terug naar Instellingen
          </Link>
        </>
      }
    >
      {/* Message */}
      {msg && (
        <div className={styles.alertInfo} style={{ marginBottom: '1rem' }}>
          {msg}
          <button
            type="button"
            onClick={() => setMsg(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* EXACT Morris beginbalans-grid */}
      <div className={styles.beginbalansGrid}>
        {/* ACTIVA */}
        <div className={styles.card}>
          <h3>Activa</h3>

          {/* Liquide middelen */}
          {renderCategorieSection('liquide_middelen', data?.activa?.liquide_middelen, 'Liquide middelen')}

          {/* Vaste activa */}
          {renderCategorieSection('vaste_activa', data?.activa?.vaste_activa, 'Vaste activa')}

          {/* Vorderingen */}
          {renderCategorieSection('vorderingen', data?.activa?.vorderingen, 'Vorderingen')}

          {/* BTW / Belasting */}
          {(data?.activa?.btw || data?.activa?.belasting) && (
            <>
              <h4 className={styles.sectionHeader}>Omzetbelasting</h4>
              <table className={`${styles.dataTable} ${styles.compact}`}>
                <tbody>
                  {renderItems([...(data?.activa?.btw || []), ...(data?.activa?.belasting || [])])}
                </tbody>
              </table>
            </>
          )}

          <div className={styles.totaalRegel}>
            <strong>Totaal Activa</strong>
            <strong>{formatBedrag(data?.totaal_activa || 0)}</strong>
          </div>
        </div>

        {/* PASSIVA */}
        <div className={styles.card}>
          <h3>Passiva</h3>

          {/* Eigen vermogen */}
          {renderCategorieSection('eigen_vermogen', data?.passiva?.eigen_vermogen, 'Eigen vermogen')}

          {/* Reserves */}
          {renderCategorieSection('reserves', data?.passiva?.reserves, 'Reserves')}

          {/* Langlopende schulden */}
          {renderCategorieSection('langlopende_schulden', data?.passiva?.langlopende_schulden, 'Langlopende schulden')}

          {/* Kortlopende schulden */}
          {(data?.passiva?.kortlopende_schulden || data?.passiva?.schulden) && (
            <>
              <h4 className={styles.sectionHeader}>Kortlopende schulden</h4>
              <table className={`${styles.dataTable} ${styles.compact}`}>
                <tbody>
                  {renderItems([...(data?.passiva?.kortlopende_schulden || []), ...(data?.passiva?.schulden || [])])}
                </tbody>
              </table>
            </>
          )}

          <div className={styles.totaalRegel}>
            <strong>Totaal Passiva</strong>
            <strong>{formatBedrag(data?.totaal_passiva || 0)}</strong>
          </div>
        </div>
      </div>

      {/* Nieuw item toevoegen - EXACT Morris */}
      <div className={styles.card} style={{ maxWidth: '700px', margin: '1.5rem auto 0' }}>
        <h3>Nieuw item toevoegen</h3>
        <form onSubmit={handleNieuwItem} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="type">Type</label>
              <select
                id="type"
                value={nieuwType}
                onChange={(e) => setNieuwType(e.target.value as 'activa' | 'passiva')}
                required
              >
                <option value="activa">Activa</option>
                <option value="passiva">Passiva</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="categorie">Categorie</label>
              <select
                id="categorie"
                value={nieuwCategorie}
                onChange={(e) => setNieuwCategorie(e.target.value)}
                required
              >
                {(nieuwType === 'activa' ? activaCategorieen : passivaCategorieen).map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={`${styles.formGroup} ${styles.formGroupLarge}`}>
              <label htmlFor="omschrijving">Omschrijving</label>
              <input
                type="text"
                id="omschrijving"
                value={nieuwOmschrijving}
                onChange={(e) => setNieuwOmschrijving(e.target.value)}
                required
                placeholder="bijv. Bank, Debiteuren, Kapitaal"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="bedrag">Bedrag</label>
              <input
                type="number"
                step="0.01"
                id="bedrag"
                value={nieuwBedrag}
                onChange={(e) => setNieuwBedrag(e.target.value)}
                required
                placeholder="0.00"
              />
            </div>
          </div>
          <div className={styles.formActions}>
            <button type="submit" className={`${styles.btn} ${styles.btnSuccess}`}>Toevoegen</button>
          </div>
        </form>
      </div>
    </BoekhoudingLayout>
  );
};

export default BeginbalansPage;
